import React, { useEffect, useState } from "react";
import {
  Container, Typography, Box, Paper, TextField, Button,
  Grid, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton
} from "@mui/material";
import {
  collection, doc, getDoc, getDocs, setDoc,
  updateDoc, increment, Timestamp, arrayUnion
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";

const IRAQ_CITIES = [
  "بغداد", "أربيل", "البصرة", "الموصل", "النجف", "كربلاء", "ذي قار", 
  "كركوك", "الأنبار", "ديالى", "المثنى", "القادسية", "ميسان", "واسط", 
  "صلاح الدين", "دهوك", "السليمانية", "بابل"
];

// قائمة الشركاء (يمكن تحديثها من هنا)
const PARTNERS_LIST = {
  "بغداد": ["شريك بغداد 1", "شريك بغداد 2"],
  "أربيل": ["الريف كافيه", "شريك أربيل 2"],
};

const SIZES = [
  "30x40", "40x60", "50x70", "60x80", "70x90", "70x100", "80x120",
  "120x180", "160x240", "30x30", "40x40", "50x50", "60x60", "70x70",
  "80x80", "90x90", "100x100", "110x110", "120x120", "130x130",
];

const SecondaryPartnerInventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // بيانات الشريك الثانوي
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [manager, setManager] = useState("");
  const [phone, setPhone] = useState("");
  const [partner, setPartner] = useState("");
  const [category, setCategory] = useState(""); 
  
  // بيانات التابلت
  const [tabletDetails, setTabletDetails] = useState("");

  // بيانات اللوحة
  const [paintingType, setPaintingType] = useState(""); 
  const [frameType, setFrameType] = useState(""); 
  const [frameColor, setFrameColor] = useState("");
  const [size, setSize] = useState("");

  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");

  const rawUser = localStorage.getItem("username");
  const isAdmin = rawUser === "admin";

  useEffect(() => { fetchInventory(); }, []);

  const fetchInventory = async () => {
    try {
        const snap = await getDocs(collection(db, "secondaryPartnerInventory"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const sortedData = data.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
        setInventory(sortedData);
    } catch (e) { console.error(e); }
  };

  const resetFields = () => {
    setCategory(""); setTabletDetails(""); setPaintingType(""); setFrameType("");
    setFrameColor(""); setSize(""); setQty(""); setPrice("");
  };

  const handleSaveNew = async () => {
    if (!city || !partner || !category || !qty) return alert("⚠️ أكمل الحقول الأساسية");
    
    const note = window.prompt("📝 سبب الإضافة (إجباري):");
    if (!note) return alert("❌ يجب ذكر السبب لإتمام العملية");

    const enteredQty = Number(qty);
    // تكوين معرف فريد مبني على التفاصيل لضمان عدم تداخل الأصناف
    let finalId = `${city}_${partner}_${category}_${paintingType}_${size}_${tabletDetails}`.replace(/\s+/g, '_');

    const ref = doc(db, "secondaryPartnerInventory", finalId);
    let logEntry = { 
      type: "إضافة جديد", 
      quantity: enteredQty, 
      date: new Date().toLocaleString("en-US"), 
      reason: note 
    };

    let dataToSave = {
      city, address, manager, phone, partner, category,
      tabletDetails, paintingType, frameType, frameColor, size,
      quantity: increment(enteredQty),
      updatedAt: Timestamp.now(),
      logs: arrayUnion(logEntry)
    };
    if (isAdmin && price) dataToSave.price = Number(price);

    await setDoc(ref, dataToSave, { merge: true });
    alert("✅ تم الحفظ بنجاح");
    resetFields();
    fetchInventory();
  };

  const handleQuickUpdate = async (item, type) => {
    const val = window.prompt(type === "add" ? "كمية الإضافة:" : "كمية الخصم:", "1");
    if (!val || isNaN(val) || Number(val) <= 0) return;
    const note = window.prompt("📝 السبب؟ (إجباري):");
    if (!note) return alert("❌ السبب مطلوب!");

    const change = Number(val);
    if (type === "remove" && change > item.quantity) return alert("❌ الكمية غير كافية");

    const ref = doc(db, "secondaryPartnerInventory", item.id);
    await updateDoc(ref, {
      quantity: increment(type === "add" ? change : -change),
      updatedAt: Timestamp.now(),
      logs: arrayUnion({ type: type === "add" ? "زيادة" : "خصم", quantity: change, date: new Date().toLocaleString("en-US"), reason: note })
    });
    fetchInventory();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={900} color="primary">🏢 مخزن الشركاء الثانويين</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>رجوع ⬅</Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 4, borderTop: '6px solid #1976d2' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>إضافة شريك ثانوي جديد</Typography>
            
            <TextField select fullWidth label="المحافظة" value={city} onChange={(e) => setCity(e.target.value)} sx={{ mb: 2 }}>
              {IRAQ_CITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>

            <TextField fullWidth label="العنوان بالتفصيل" value={address} onChange={(e) => setAddress(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth label="اسم الشخص المسؤول" value={manager} onChange={(e) => setManager(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth label="رقم الموبايل" value={phone} onChange={(e) => setPhone(e.target.value)} sx={{ mb: 2 }} />

            {city && (
              <TextField select fullWidth label="اسم الشريك" value={partner} onChange={(e) => setPartner(e.target.value)} sx={{ mb: 2 }}>
                {(PARTNERS_LIST[city] || []).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                <MenuItem value="other">أخرى / شريك جديد</MenuItem>
              </TextField>
            )}

            <TextField select fullWidth label="نوع المادة" value={category} onChange={(e) => { resetFields(); setCategory(e.target.value); }} sx={{ mb: 2 }}>
              <MenuItem value="تابليت">تابليت (اختياري)</MenuItem>
              <MenuItem value="لوحة">لوحة</MenuItem>
            </TextField>

            {category === "تابليت" && (
              <TextField fullWidth multiline rows={2} label="تفاصيل التابليت" value={tabletDetails} onChange={(e) => setTabletDetails(e.target.value)} placeholder="مثال: تابليت سامسونغ A09 قياس ١٣" sx={{ mb: 2 }} />
            )}

            {category === "لوحة" && (
              <>
                <TextField select fullWidth label="نوع اللوحة" value={paintingType} onChange={(e) => setPaintingType(e.target.value)} sx={{ mb: 2 }}>
                  <MenuItem value="اورجن">اورجن</MenuItem>
                  <MenuItem value="ادشن">ادشن</MenuItem>
                  <MenuItem value="برايم">برايم</MenuItem>
                  <MenuItem value="جام">جام</MenuItem>
                </TextField>
                <TextField select fullWidth label="نوع الإطار" value={frameType} onChange={(e) => setFrameType(e.target.value)} sx={{ mb: 2 }}>
                  <MenuItem value="إيل">إيل</MenuItem>
                  <MenuItem value="كاس">كاس</MenuItem>
                </TextField>
                <TextField fullWidth label="لون الإطار" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} sx={{ mb: 2 }} />
                <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}>
                  {SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </>
            )}

            {category && (
              <>
                <TextField fullWidth type="number" label="العدد" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ mb: 2 }} />
                {isAdmin && <TextField fullWidth label="السعر" type="number" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ mb: 3 }} />}
                <Button fullWidth variant="contained" size="large" onClick={handleSaveNew} sx={{ borderRadius: 3, fontWeight: 'bold' }}>💾 حفظ البيانات</Button>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #eee' }}>
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
              <Typography variant="h6" fontWeight={800}>📊 سجل مخزن الشركاء الثانويين</Typography>
              <TextField size="small" placeholder="بحث باسم الشريك..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: 200 }} />
            </Box>

            <TableContainer sx={{ maxHeight: 650, borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>الشريك / المسؤول</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>التفاصيل</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>الكمية</TableCell>
                    {isAdmin && <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>السعر</TableCell>}
                    <TableCell align="right" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>تحديث</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory
                    .filter(i => (i.partner || "").includes(searchQuery))
                    .map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">{item.partner}</Typography>
                          <Typography variant="caption" color="textSecondary">{item.manager} | {item.phone}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{item.category}: {item.paintingType || ""} {item.tabletDetails || ""}</Typography>
                          <Typography variant="caption" color="primary">{item.size} {item.frameType} {item.frameColor}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <IconButton size="small" color="error" onClick={() => handleQuickUpdate(item, "remove")}>➖</IconButton>
                            <Typography fontWeight="bold">{item.quantity}</Typography>
                            <IconButton size="small" color="success" onClick={() => handleQuickUpdate(item, "add")}>➕</IconButton>
                          </Box>
                        </TableCell>
                        {isAdmin && <TableCell align="right">{item.price?.toLocaleString()} د.ع</TableCell>}
                        <TableCell align="right" sx={{ fontSize: '10px' }}>
                          {item.updatedAt?.toDate ? item.updatedAt.toDate().toLocaleString('en-US', { dateStyle: 'short' }) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SecondaryPartnerInventory;